import type { RecordApiKind, RecordHistory, ReviewEventDto } from '@fruuit/shared'
import { Button, EmptyState, SkeletonText, StatusBadge, statusTone, type Tone } from '@fruuit/ui'
import { History } from 'lucide-react'
import { errorMessage } from '../../api/client'
import { useRecordHistory } from './api'
import { formatDate, formatDateTime } from './format'

const MARKERS: Record<Tone, string> = {
  positive: 'bg-positive',
  attention: 'bg-attention',
  watch: 'bg-watch',
  quiet: 'bg-teal-300',
  info: 'bg-teal',
}

const EVENT_TITLES: Record<string, string> = {
  create: 'Draft created',
  edit: 'Draft edited',
  submit: 'Submitted for review',
  approve: 'Independently approved',
  publish: 'Published to clients',
  return: 'Returned for changes',
  retract: 'Retracted from client view',
  release: 'Released to clients',
  correct: 'Correction started',
}

function eventTitle(event: ReviewEventDto): string {
  return EVENT_TITLES[event.action] ?? event.action
}

export interface HistoryPanelProps {
  kind: RecordApiKind
  id: string
  /** The version currently shown, highlighted in the list. */
  currentId?: string
  onOpenVersion?: (id: string) => void
  /** Compare a version with the one it corrects. */
  onCompare?: (id: string, previousId: string) => void
  /** Use already-loaded history instead of fetching. */
  history?: RecordHistory
}

/** Version chain and append-only review events (P-WF-003, GAP-DD-007). */
export function HistoryPanel({
  kind,
  id,
  currentId,
  onOpenVersion,
  onCompare,
  history: given,
}: HistoryPanelProps) {
  const query = useRecordHistory(kind, given ? null : id)
  const history = given ?? query.data
  if (!history) {
    if (query.isError) {
      return <p className="text-sm text-danger">{errorMessage(query.error)}</p>
    }
    return <SkeletonText lines={4} />
  }
  const versions = [...history.versions].reverse()
  const versionNumbers = new Map(history.versions.map((version) => [version.id, version.version]))
  const events = [...history.events].sort((a, b) => b.created_at.localeCompare(a.created_at))
  return (
    <div className="flex flex-col gap-6">
      {versions.length > 1 || versions[0]?.previous_id ? (
        <section aria-label="Versions">
          <h3 className="mb-2 text-xs font-semibold tracking-[0.08em] text-sage uppercase">
            Versions
          </h3>
          <ul className="divide-y divide-line rounded-md border border-line">
            {versions.map((version) => (
              <li
                key={version.id}
                aria-current={version.id === currentId ? 'true' : undefined}
                className={`flex flex-col gap-1.5 px-3 py-2.5 text-sm ${
                  version.id === currentId ? 'bg-wash/70' : ''
                }`}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-semibold text-ink">v{version.version}</span>
                  <StatusBadge status={version.state} />
                  <span className="ml-auto text-xs text-sage">
                    {version.published_at
                      ? `Published ${formatDate(version.published_at)}`
                      : `Created ${formatDate(version.created_at)}`}
                  </span>
                </div>
                <p className="text-ink-soft">{version.change_reason ?? '—'}</p>
                <span className="-ml-2 flex flex-wrap gap-1">
                  {onOpenVersion && version.id !== currentId ? (
                    <Button size="sm" variant="ghost" onClick={() => onOpenVersion(version.id)}>
                      Open
                    </Button>
                  ) : null}
                  {onCompare && version.previous_id ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onCompare(version.id, version.previous_id as string)}
                    >
                      Compare with{' '}
                      {versionNumbers.has(version.previous_id)
                        ? `v${versionNumbers.get(version.previous_id)}`
                        : 'previous'}
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section aria-label="Review history">
        <h3 className="mb-3 text-xs font-semibold tracking-[0.08em] text-sage uppercase">
          Review history
        </h3>
        {events.length === 0 ? (
          <EmptyState
            variant="plain"
            icon={<History />}
            title="No review events yet"
            description="Every submission, approval, publication, return and retraction is recorded here."
          />
        ) : (
          <ol aria-label="Review history" className="relative flex flex-col">
            {events.map((event, index) => (
              <li key={event.id} className="relative pb-5 pl-6 last:pb-0">
                {index < events.length - 1 ? (
                  <span
                    aria-hidden="true"
                    className="absolute top-3 bottom-0 left-[5px] w-px bg-line"
                  />
                ) : null}
                <span
                  aria-hidden="true"
                  className={`absolute top-[6px] left-0 size-[11px] rounded-full ring-[3px] ring-surface ${MARKERS[statusTone(event.to_state)]}`}
                />
                <p className="text-sm font-semibold text-ink">
                  {eventTitle(event)}{' '}
                  <span className="font-normal text-sage">· v{event.record_version}</span>
                </p>
                <p className="text-xs text-sage">
                  {formatDateTime(event.created_at)} · {event.actor_name ?? 'Unknown user'}
                </p>
                {event.reason ? (
                  <p className="mt-1 text-sm leading-relaxed whitespace-pre-line text-ink-soft">
                    {event.reason}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}
